import React from "react";
import type { CanvasGridConfig } from "../types";
import type { CanvasKeyboardStep } from "./canvasSensors";

type KeyboardStepGridConfig = Pick<CanvasGridConfig, "columns" | "gap" | "rowHeight">;

export function measureCanvasKeyboardStep(
  width: number,
  gridConfig: KeyboardStepGridConfig,
): CanvasKeyboardStep | null {
  if (width <= 0) {
    return null;
  }
  const cellWidth = (width - (gridConfig.columns - 1) * gridConfig.gap) / gridConfig.columns;
  return {
    x: cellWidth + gridConfig.gap,
    y: gridConfig.rowHeight + gridConfig.gap,
  };
}

export function useMeasuredCanvasKeyboardStep(gridConfig: KeyboardStepGridConfig): {
  canvasAreaRef: (node: HTMLDivElement | null) => void;
  canvasAreaNodeRef: React.MutableRefObject<HTMLDivElement | null>;
  keyboardStep: CanvasKeyboardStep | null;
} {
  const { columns, gap, rowHeight } = gridConfig;
  const canvasAreaNodeRef = React.useRef<HTMLDivElement | null>(null);
  const [canvasAreaNode, setCanvasAreaNode] = React.useState<HTMLDivElement | null>(null);
  const [keyboardStep, setKeyboardStep] = React.useState<CanvasKeyboardStep | null>(null);

  const canvasAreaRef = React.useCallback((node: HTMLDivElement | null) => {
    canvasAreaNodeRef.current = node;
    setCanvasAreaNode((current) => (current === node ? current : node));
  }, []);

  React.useLayoutEffect(() => {
    if (!canvasAreaNode) {
      setKeyboardStep(null);
      return;
    }

    let rafId = 0;
    const measure = () => {
      const nextStep = measureCanvasKeyboardStep(
        canvasAreaNode.getBoundingClientRect().width,
        { columns, gap, rowHeight },
      );
      if (!nextStep) {
        return;
      }
      setKeyboardStep((current) => {
        if (current?.x === nextStep.x && current?.y === nextStep.y) {
          return current;
        }
        return nextStep;
      });
    };

    measure();
    rafId = window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(canvasAreaNode);

    return () => {
      window.cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [canvasAreaNode, columns, gap, rowHeight]);

  return { canvasAreaRef, canvasAreaNodeRef, keyboardStep };
}
